import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup"; // Importando Yup para validação
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon } from "@material-ui/icons";

import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import { head, isArray } from "lodash";
import { useDate } from "../../hooks/useDate";

import moment from "moment";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: "2px"
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    // padding: theme.spacing(2), //comentado para retirar o scroll do Empresas
  },
  fullWidth: {
    width: "100%",
  },
  tableContainer: {
    width: "100%",
    // overflowX: "scroll",
    // ...theme.scrollbarStyles,
    padding: "2px",
  },
  textfield: {
    width: "100%",
  },
  textRight: {
    textAlign: "right",
  },
  row: {
    // paddingTop: theme.spacing(2),
    // paddingBottom: theme.spacing(2),
  },
  control: {
    // paddingRight: theme.spacing(1),
    // paddingLeft: theme.spacing(1),
  },
  buttonContainer: {
    textAlign: "right",
    // padding: theme.spacing(1),
  },
}));

// Definição do esquema de validação com Yup
const validationSchema = Yup.object().shape({
  name: Yup.string().required(i18n.t("validation.nameRequired")),
  email: Yup.string()
    .email(i18n.t("validation.invalidEmail"))
    .required(i18n.t("validation.emailRequired")),
  password: Yup.string()
    .min(5, i18n.t("validation.passwordMin"))
    .when('id', {
      is: (val) => !val, // Se não houver id, significa que estamos criando
      then: Yup.string().required(i18n.t("validation.passwordRequired")),
      otherwise: Yup.string().notRequired(),
    }),
    phone: Yup.string()
  .matches(/^\+?\d{7,15}$/, "Telefone inválido. Use de 7 a 15 dígitos, com ou sem +")
  .notRequired(),
  planId: Yup.string().required(i18n.t("validation.planRequired")),
  dueDate: Yup.date()
    .required(i18n.t("validation.dueDateRequired"))
    .nullable(),
  recurrence: Yup.string()
    .when('id', {
      is: (val) => !val, // Se não houver id, estamos criando e recorrência é obrigatória
      then: Yup.string().required(i18n.t("validation.recurrenceRequired")),
      otherwise: Yup.string().notRequired(),
    }),
  document: Yup.string().notRequired(),
  paymentMethod: Yup.string().notRequired(),
});

export function CompanyForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", {
        params: {
          companyId: initialValue.id,
        },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e.message || "Erro ao carregar usuários.");
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = (currentDueDate, recurrence) => {
    let newDueDate = moment(currentDueDate, "YYYY-MM-DD");
    switch (recurrence) {
      case "MENSAL":
        newDueDate = newDueDate.add(1, "month");
        break;
      case "BIMESTRAL":
        newDueDate = newDueDate.add(2, "month");
        break;
      case "TRIMESTRAL":
        newDueDate = newDueDate.add(3, "month");
        break;
      case "SEMESTRAL":
        newDueDate = newDueDate.add(6, "month");
        break;
      case "ANUAL":
        newDueDate = newDueDate.add(12, "month");
        break;
      default:
        toast.error("Recorrência inválida.");
        return currentDueDate;
    }
    return newDueDate.format("YYYY-MM-DD");
  };

  // Determina se estamos editando ou criando
  const isEditing = !!initialValue.id;

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        className={classes.fullWidth}
        initialValues={{
          id: initialValue.id || undefined,
          name: initialValue.name || "",
          email: initialValue.email || "",
          phone: initialValue.phone || "",
          planId: initialValue.planId || "",
          status: initialValue.status !== undefined ? initialValue.status : true,
          dueDate: initialValue.dueDate
            ? moment(initialValue.dueDate).format("YYYY-MM-DD")
            : "",
          recurrence: initialValue.recurrence || "",
          password: "", // Sempre iniciar como vazio
          document: initialValue.document || "",
          paymentMethod: initialValue.paymentMethod || "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          onSubmit(values, { resetForm });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form className={classes.fullWidth}>
            <Grid spacing={1} justifyContent="center" container>
              <Grid xs={12} sm={6} md={3} item>
                <TextField
                  label={i18n.t("compaies.table.name")}
                  name="name"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  required
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  label={i18n.t("compaies.table.email")}
                  name="email"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  required
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  label={i18n.t("compaies.table.password")}
                  name="password"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  required={!isEditing}
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  label={i18n.t("compaies.table.phone")}
                  name="phone"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                  //inputProps={{ maxLength: 11 }}
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  error={touched.planId && Boolean(errors.planId)}
                  required
                >
                  <InputLabel id="plan-selection-label">
                    {i18n.t("compaies.table.plan")}
                  </InputLabel>
                  <Select
                    labelId="plan-selection-label"
                    id="plan-selection"
                    name="planId"
                    value={values.planId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={i18n.t("compaies.table.plan")}
                  >
                    {plans.map((plan, key) => (
                      <MenuItem key={key} value={plan.id}>
                        {plan.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.planId && errors.planId && (
                    <div
                      style={{
                        color: "#f44336",
                        marginTop: "4px",
                        fontSize: "0.75rem",
                      }}
                    >
                      {errors.planId}
                    </div>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={1} item>
                <FormControl
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  error={touched.status && Boolean(errors.status)}
                >
                  <InputLabel id="status-selection-label">
                    {i18n.t("compaies.table.active")}
                  </InputLabel>
                  <Select
                    labelId="status-selection-label"
                    id="status-selection"
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={i18n.t("compaies.table.active")}
                  >
                    <MenuItem value={true}>
                      {i18n.t("compaies.table.yes")}
                    </MenuItem>
                    <MenuItem value={false}>
                      {i18n.t("compaies.table.no")}
                    </MenuItem>
                  </Select>
                  {touched.status && errors.status && (
                    <div
                      style={{
                        color: "#f44336",
                        marginTop: "4px",
                        fontSize: "0.75rem",
                      }}
                    >
                      {errors.status}
                    </div>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <TextField
                  label={i18n.t("compaies.table.document")}
                  name="document"
                  variant="outlined"
                  className={classes.fullWidth}
                  margin="dense"
                  value={values.document}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl variant="outlined" fullWidth>
                  <TextField
                    label={i18n.t("compaies.table.dueDate")}
                    type="date"
                    name="dueDate"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    value={values.dueDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.dueDate && Boolean(errors.dueDate)}
                    helperText={touched.dueDate && errors.dueDate}
                    required
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6} md={2} item>
                <FormControl
                  margin="dense"
                  variant="outlined"
                  fullWidth
                  error={touched.recurrence && Boolean(errors.recurrence)}
                  required={!isEditing}
                >
                  <InputLabel id="recorrencia-selection-label">
                    {i18n.t("compaies.table.recurrence")}
                  </InputLabel>
                  <Select
                    labelId="recorrencia-selection-label"
                    id="recurrence"
                    name="recurrence"
                    value={values.recurrence}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={i18n.t("compaies.table.recurrence")}
                  >
                    <MenuItem value="MENSAL">
                      {i18n.t("compaies.table.monthly")}
                    </MenuItem>
                    <MenuItem value="BIMESTRAL">
                      {i18n.t("compaies.table.bimonthly")}
                    </MenuItem>
                    <MenuItem value="TRIMESTRAL">
                      {i18n.t("compaies.table.quarterly")}
                    </MenuItem>
                    <MenuItem value="SEMESTRAL">
                      {i18n.t("compaies.table.semester")}
                    </MenuItem>
                    <MenuItem value="ANUAL">
                      {i18n.t("compaies.table.yearly")}
                    </MenuItem>
                  </Select>
                  {touched.recurrence && errors.recurrence && (
                    <div
                      style={{
                        color: "#f44336",
                        marginTop: "4px",
                        fontSize: "0.75rem",
                      }}
                    >
                      {errors.recurrence}
                    </div>
                  )}
                </FormControl>
              </Grid>
              <Grid xs={12} item>
                <Grid justifyContent="flex-end" spacing={1} container>
                  <Grid xs={4} md={1} item>
                    <ButtonWithSpinner
                      className={classes.fullWidth}
                      style={{ marginTop: 7 }}
                      loading={loading}
                      onClick={() => onCancel()}
                      variant="contained"
                    >
                      {i18n.t("compaies.table.clear")}
                    </ButtonWithSpinner>
                  </Grid>
                  {isEditing ? (
                    <>
                      <Grid xs={6} md={1} item>
                        <ButtonWithSpinner
                          style={{ marginTop: 7 }}
                          className={classes.fullWidth}
                          loading={loading}
                          onClick={() => onDelete(values)}
                          variant="contained"
                          color="secondary"
                        >
                          {i18n.t("compaies.table.delete")}
                        </ButtonWithSpinner>
                      </Grid>
                      <Grid xs={6} md={2} item>
                        <ButtonWithSpinner
                          style={{ marginTop: 7 }}
                          className={classes.fullWidth}
                          loading={loading}
                          onClick={() => {
                            if (!values.dueDate || !values.recurrence) {
                              toast.error(
                                "Selecione uma data e recorrência válidas para incrementar."
                              );
                              return;
                            }
                            const newDueDate = incrementDueDate(
                              values.dueDate,
                              values.recurrence
                            );
                            setFieldValue("dueDate", newDueDate);
                            toast.success("Data de vencimento atualizada.");
                          }}
                          variant="contained"
                          color="primary"
                        >
                          {i18n.t("RENOVAR")}
                        </ButtonWithSpinner>
                      </Grid>
                      {/* <Grid xs={6} md={1} item>
                        <ButtonWithSpinner
                          style={{ marginTop: 7 }}
                          className={classes.fullWidth}
                          loading={loading}
                          onClick={() => handleOpenModalUsers()}
                          variant="contained"
                          color="primary"
                        >
                          {i18n.t("compaies.table.user")}
                        </ButtonWithSpinner>
                      </Grid> */}
                    </>
                  ) : null}
                  <Grid xs={6} md={1} item>
                    <ButtonWithSpinner
                      className={classes.fullWidth}
                      style={{ marginTop: 7 }}
                      loading={loading}
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      {i18n.t("compaies.table.save")}
                    </ButtonWithSpinner>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
}

export function CompaniesManagerGrid(props) {
  const { records, onSelect } = props;
  const classes = useStyles();
  const { dateToClient, datetimeToClient } = useDate();

  const renderStatus = (row) => {
    return row.status === false ? "Não" : "Sim";
  };

  const renderPlan = (row) => {
    return row.planId !== null ? row.plan.name : "-";
  };

  const renderPlanValue = (row) => {
    return row.planId !== null
      ? row.plan.amount
        ? row.plan.amount.toLocaleString("pt-br", {
            minimumFractionDigits: 2,
          })
        : "00.00"
      : "-";
  };

  // const renderCampaignsStatus = (row) => {
  //   if (
  //     has(row, "settings") &&
  //     isArray(row.settings) &&
  //     row.settings.length > 0
  //   ) {
  //     const setting = row.settings.find((s) => s.key === "campaignsEnabled");
  //     if (setting) {
  //       return setting.value === "true" ? "Habilitadas" : "Desabilitadas";
  //     }
  //   }
  //   return "Desabilitadas";
  // };

  const rowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { backgroundColor: "#EEC911" };
      }
      if (diff <= 0) {
        return { backgroundColor: "#E72424" };
      } else {
        return { backgroundColor: "#12AE0A" };
      }
    }
    return {};
  };

  return (
    <Paper className={classes.tableContainer}>
      <Table
        className={classes.fullWidth}
        // size="small"
        padding="none"
        aria-label="a dense table"
      >
        <TableHead>
          <TableRow>
            <TableCell align="center" style={{ width: "1%" }}>#</TableCell>
            <TableCell align="left">{i18n.t("compaies.table.name")}</TableCell>
            <TableCell align="left">{i18n.t("compaies.table.email")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.phone")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.plan")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.value")}</TableCell>
            {/* <TableCell align="center">Campanhas</TableCell> */}
            <TableCell align="center">{i18n.t("compaies.table.active")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.createdAt")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.dueDate")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.lastLogin")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow style={rowStyle(row)} key={key}>
              <TableCell align="center" style={{ width: "1%" }}>
                <IconButton onClick={() => onSelect(row)} aria-label="edit">
                  <EditIcon />
                </IconButton>
              </TableCell>
              <TableCell align="left">{row.name || "-"}</TableCell>
              <TableCell align="left" size="small">{row.email || "-"}</TableCell>
              <TableCell align="center">{row.phone || "-"}</TableCell>
              <TableCell align="center">{renderPlan(row)}</TableCell>
              <TableCell align="center">{i18n.t("compaies.table.money")} {renderPlanValue(row)}</TableCell>
              {/* <TableCell align="center">{renderCampaignsStatus(row)}</TableCell> */}
              <TableCell align="center">{renderStatus(row)}</TableCell>
              <TableCell align="center">{dateToClient(row.createdAt)}</TableCell>
              <TableCell align="center">{dateToClient(row.dueDate)}<br /><span>{row.recurrence}</span></TableCell>
              <TableCell align="center">{datetimeToClient(row.lastLogin)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function CompaniesManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    password: "",
    document: "",
    paymentMethod: ""
  });

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      setRecords(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoading(false);
  };

  const handleSubmit = async (data, { resetForm }) => {
    setLoading(true);
    try {
      // Cria uma cópia dos dados para evitar mutação direta
      let payload = { ...data };

      // Verifica se está editando e se a senha está vazia ou undefined
      if (payload.id !== undefined && !payload.password) {
        delete payload.password; // Remove o campo password
      }

      if (payload.id !== undefined) {
        await update(payload);
      } else {
        await save(payload);
      }
      await loadCompanies();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
      resetForm();
    } catch (e) {
      toast.error(
        "Não foi possível realizar a operação. Verifique se já existe uma empresa com o mesmo nome ou se os campos foram preenchidos corretamente"
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadCompanies();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação");
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord({
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      dueDate: "",
      recurrence: "",
      password: "",
      document: "",
      paymentMethod: "",
    });
  };

  const handleSelect = (data) => {
    setRecord({
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      dueDate: data.dueDate
        ? moment(data.dueDate).format("YYYY-MM-DD")
        : "",
      recurrence: data.recurrence || "",
      password: "", // Mantém como vazio ao selecionar para editar
      document: data.document || "",
      paymentMethod: data.paymentMethod || "",
    });
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <CompanyForm
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
        <Grid xs={12} item>
          <CompaniesManagerGrid records={records} onSelect={handleSelect} />
        </Grid>
      </Grid>
      <ConfirmationModal
        title="Exclusão de Registro"
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
    </Paper>
  );
}
